<template>
  <SettingsLayout>
    <div class="information-view">
      <div class="container">
        <section v-if="isFetchingVendor || isUpdatingVendor">
          <spinner-loader class="centerscreen" />
        </section>

        <section v-else-if="errored">
          <p>An error occurred.</p>
        </section>

        <section v-else>
          <div class="d-flex justify-content-between align-items-center mb-4">
            <h2 class="mb-0">{{ $t("Hotel Information") }}</h2>
            <b-button variant="primary" @click="handleSubmit">
              {{
                isEditing ? $t("save").toUpperCase() : $t("edit").toUpperCase()
              }}
            </b-button>
          </div>

          <b-form @submit="handleSubmit">
            <!-- Basic Information Card -->
            <b-card class="mb-4">
              <h4 class="card-title border-bottom pb-2 mb-3">
                {{ $t("Basic Information") }}
              </h4>

              <div class="d-flex mb-4">
                <div class="property-info flex-grow-1">
                  <!-- Property Name -->
                  <b-form-group
                    :label="$t('Property Name')"
                    label-for="property-name"
                  >
                    <b-form-input
                      id="property-name"
                      v-model="formData.name"
                      :placeholder="$t('Property Name')"
                      type="text"
                      required
                      :disabled="!isEditing"
                    />
                  </b-form-group>

                  <!-- Branding Accent Color -->
                  <b-form-group
                    :label="$t('Branding Accent')"
                    label-for="brand-accent"
                  >
                    <div class="color-picker-wrapper">
                      <span
                        class="current-color"
                        :class="{ disabled: !isEditing }"
                        :style="
                          'background-color: ' + formData.brand_accent_color
                        "
                        @click="isEditing ? toggleColorPicker($event) : null"
                      ></span>
                      <span class="color-value">{{
                        formData.brand_accent_color
                      }}</span>
                      <sketch-picker
                        v-if="showPicker && isEditing"
                        :value="formData.brand_accent_color"
                        disableAlpha
                        @input="updateColor"
                      />
                    </div>
                  </b-form-group>

                  <!-- Address -->
                  <b-form-group
                    :label="$t('Address')"
                    label-for="address-input"
                  >
                    <b-form-input
                      v-model="formData.latitude"
                      class="hidden"
                      type="text"
                    />
                    <b-form-input
                      v-model="formData.longitude"
                      class="hidden"
                      type="text"
                    />
                    <google-address-input
                      id="address-input"
                      :place="formData.address"
                      @update="updateAddress"
                      :disabled="!isEditing"
                    />
                  </b-form-group>

                  <!-- Currency & Language -->
                  <div class="row">
                    <div class="col-md-6 mb-3">
                      <label>{{ $t("Currency") }}</label>
                      <b-form-select
                        v-model="formData.local_currency"
                        :options="currency_options"
                        :disabled="!isEditing"
                      ></b-form-select>
                    </div>
                    <div class="col-md-6 mb-3">
                      <label>{{ $t("Language") }}</label>
                      <locale-switcher />
                    </div>
                  </div>

                  <!-- Description -->
                  <div class="mb-3">
                    <label>{{ $t("Description") }}</label>

                    <b-form-textarea
                      v-model="formData.description"
                      :placeholder="$t('description_placeholder').toLowerCase()"
                      maxLength="255"
                      :disabled="!isEditing"
                    />
                  </div>
                </div>

                <!-- Images Column -->
                <div class="ml-4" style="width: 220px">
                  <!-- Hotel Logo -->
                  <div class="mb-3">
                    <label>{{ $t("Hotel Logo") }}</label>
                    <div class="hotel-information-image-container logo-image">
                      <photo-upload-container
                        :image-uri="formData.logo_uri"
                        @click-add="onClickFileInput('logo')"
                      >
                        <div
                          v-if="!formData.logo_uri"
                          class="image-container text-center"
                        >
                          {{ $t("Add Logo") }}
                        </div>
                      </photo-upload-container>
                    </div>
                  </div>

                  <!-- Hotel Image -->
                  <div>
                    <label>{{ $t("Hotel Image") }}</label>
                    <div class="hotel-information-image-container logo-image">
                      <photo-upload-container
                        :image-uri="formData.image_uri"
                        @click-add="onClickFileInput('photo')"
                      >
                        <div
                          v-if="!formData.image_uri"
                          class="image-container text-center"
                        >
                          {{ $t("Add Photo") }}
                        </div>
                      </photo-upload-container>
                    </div>
                  </div>
                </div>
              </div>
            </b-card>

            <!-- Contact Information Card -->
            <b-card class="mb-4">
              <h4 class="card-title border-bottom pb-2 mb-3">
                {{ $t("Contact Information") }}
              </h4>

              <div class="row">
                <!-- Website -->
                <div class="col-md-4 mb-3">
                  <label>{{ $t("Website") }}</label>
                  <p v-if="!isEditing && formData.website" class="mb-0">
                    {{ formData.website }}
                  </p>
                  <b-form-input
                    v-model="formData.website"
                    :placeholder="$t('website_placeholder').toLowerCase()"
                    type="url"
                    :disabled="!isEditing"
                  />
                </div>

                <!-- Email -->
                <div class="col-md-4 mb-3">
                  <label>{{ $t("Email") }}</label>
                  <p v-if="!isEditing && formData.email" class="mb-0">
                    {{ formData.email }}
                  </p>
                  <b-form-input
                    v-model="formData.email"
                    :placeholder="$t('email_placeholder').toLowerCase()"
                    type="email"
                    :disabled="!isEditing"
                  />
                </div>

                <!-- Phone -->
                <div class="col-md-4 mb-3">
                  <label>{{ $t("Phone") }}</label>
                  <p v-if="!isEditing && formData.phone_number" class="mb-0">
                    {{ formData.phone_number }}
                  </p>
                  <b-form-input
                    v-model="formData.phone_number"
                    :placeholder="$t('phone_placeholder').toLowerCase()"
                    type="tel"
                    :disabled="!isEditing"
                  />
                </div>
              </div>
            </b-card>

            <!-- Payment Methods Card -->
            <b-card v-if="sectionVisibility.payments" class="mb-4">
              <h4 class="card-title border-bottom pb-2 mb-3">
                {{ $t("Payment Methods") }}
              </h4>
              <div class="row">
                <div class="col-12">
                  <b-form-group :label="$t('Accepted Payment Methods')">
                    <div v-if="!isEditing" class="payment-methods">
                      <b-badge variant="info" class="mr-2">CASH</b-badge>
                      <b-badge variant="info" class="mr-2">CC</b-badge>
                      <b-badge variant="info">PAYPAL</b-badge>
                    </div>
                    <b-form-input
                      v-model="formData.accepted_payments"
                      :disabled="!isEditing"
                      :placeholder="$t('Enter accepted payment methods')"
                    />
                  </b-form-group>
                </div>
              </div>
            </b-card>

            <!-- Amenities Card -->
            <b-card v-if="sectionVisibility.amenities" class="mb-4">
              <h4 class="card-title border-bottom pb-2 mb-3">
                {{ $t("Amenities") }}
              </h4>
              <div class="row">
                <div class="col-12">
                  <b-form-group :label="$t('Available Amenities')">
                    <div v-if="!isEditing" class="amenities-list">
                      <b-badge
                        v-for="amenity in amenities"
                        :key="amenity"
                        variant="info"
                        class="mr-2 mb-2"
                      >
                        {{ amenity }}
                      </b-badge>
                    </div>
                    <b-form-input
                      v-model="formData.amenities"
                      :disabled="!isEditing"
                      :placeholder="$t('Enter amenities')"
                    />
                  </b-form-group>
                </div>
              </div>
            </b-card>

            <!-- Social Media Card -->
            <b-card v-if="sectionVisibility.socialMedia" class="mb-4">
              <h4 class="card-title border-bottom pb-2 mb-3">
                {{ $t("Social Media") }}
              </h4>

              <div class="row">
                <!-- Facebook -->
                <div class="col-md-4 mb-3">
                  <label>{{ $t("Facebook") }}</label>
                  <p v-if="!isEditing && formData.facebook" class="mb-0">
                    {{ formData.facebook }}
                  </p>
                  <b-form-input
                    v-model="formData.facebook"
                    :placeholder="$t('Facebook URL')"
                    :disabled="!isEditing"
                  />
                </div>

                <!-- Instagram -->
                <div class="col-md-4 mb-3">
                  <label>{{ $t("Instagram") }}</label>
                  <p v-if="!isEditing && formData.instagram" class="mb-0">
                    {{ formData.instagram }}
                  </p>
                  <b-form-input
                    v-model="formData.instagram"
                    :placeholder="$t('Instagram handle')"
                    :disabled="!isEditing"
                  />
                </div>

                <!-- Twitter -->
                <div class="col-md-4 mb-3">
                  <label>{{ $t("Twitter") }}</label>
                  <p v-if="!isEditing && formData.twitter" class="mb-0">
                    {{ formData.twitter }}
                  </p>
                  <b-form-input
                    v-model="formData.twitter"
                    :placeholder="$t('Twitter handle')"
                    :disabled="!isEditing"
                  />
                </div>

                <!-- Snapchat -->
                <div class="col-md-4 mb-3">
                  <label>{{ $t("Snapchat") }}</label>
                  <p v-if="!isEditing && formData.snapchat" class="mb-0">
                    {{ formData.snapchat }}
                  </p>
                  <b-form-input
                    v-model="formData.snapchat"
                    :placeholder="$t('Snapchat username')"
                    :disabled="!isEditing"
                  />
                </div>

                <!-- Pinterest -->
                <div class="col-md-4 mb-3">
                  <label>{{ $t("Pinterest") }}</label>
                  <p v-if="!isEditing && formData.pinterest" class="mb-0">
                    {{ formData.pinterest }}
                  </p>
                  <b-form-input
                    v-model="formData.pinterest"
                    :placeholder="$t('Pinterest username')"
                    :disabled="!isEditing"
                  />
                </div>
              </div>
            </b-card>

            <!-- Direct Booking Card -->
            <b-card v-if="sectionVisibility.directBooking" class="mb-4">
              <h4 class="card-title border-bottom pb-2 mb-3">
                {{ $t("Direct Booking Settings") }}
              </h4>
              <div class="row">
                <div class="col-md-6 mb-3">
                  <b-form-group :label="$t('Direct Booking')">
                    <toggle-button
                      v-model="formData.direct_booking_enabled"
                      :disabled="!isEditing"
                      :sync="true"
                      :labels="{ checked: 'On', unchecked: 'Off' }"
                      :width="100"
                      :height="33"
                      color="#82C7EB"
                    />
                  </b-form-group>
                </div>
                <div class="col-md-6 mb-3">
                  <b-form-group :label="$t('Direct Booking URL')">
                    <div class="booking-url">
                      {{ directBookingUrl }}
                    </div>
                  </b-form-group>
                </div>
                <div class="col-md-6 mb-3">
                  <b-form-group :label="$t('Discount Enabled')">
                    <toggle-button
                      v-model="formData.discount_enabled"
                      :disabled="!isEditing"
                      :sync="true"
                      :labels="{ checked: 'On', unchecked: 'Off' }"
                      :width="100"
                      :height="33"
                      color="#82C7EB"
                    />
                  </b-form-group>
                </div>
                <div class="col-md-6 mb-3">
                  <b-form-group :label="$t('Items Enabled')">
                    <toggle-button
                      v-model="formData.item_enabled"
                      :disabled="!isEditing"
                      :sync="true"
                      :labels="{ checked: 'On', unchecked: 'Off' }"
                      :width="100"
                      :height="33"
                      color="#82C7EB"
                    />
                  </b-form-group>
                </div>
                <div class="col-md-6 mb-3">
                  <b-form-group :label="$t('Email Confirmation')">
                    <toggle-button
                      v-model="formData.email_confirmation"
                      :disabled="!isEditing"
                      :sync="true"
                      :labels="{ checked: 'On', unchecked: 'Off' }"
                      :width="100"
                      :height="33"
                      color="#82C7EB"
                    />
                  </b-form-group>
                </div>
                <div class="col-md-6 mb-3">
                  <b-form-group :label="$t('Minimum Nights')">
                    <b-form-input
                      v-model="formData.min_nights"
                      :disabled="!isEditing"
                      :class="[
                        'input-info-field',
                        { 'input-error': fieldState.min_nights === false },
                      ]"
                      :field-state="fieldState.min_nights"
                      type="number"
                      min="1"
                      step="1"
                    />
                  </b-form-group>
                </div>
                <div class="col-12 mb-3">
                  <b-form-group :label="$t('Deposit Policy')">
                    <b-form-textarea
                      v-model="formData.deposit_policy"
                      :disabled="!isEditing"
                      :placeholder="$t('Enter deposit policy')"
                    />
                  </b-form-group>
                </div>
                <div class="col-md-6 mb-3">
                  <b-form-group :label="$t('Enable Payment')">
                    <toggle-button
                      v-model="formData.payment_enabled"
                      :disabled="!isEditing || !formData.stripe_id"
                      :sync="true"
                      :labels="{ checked: 'Yes', unchecked: 'No' }"
                      :width="100"
                      :height="33"
                      color="#82C7EB"
                    />
                  </b-form-group>
                </div>
              </div>
            </b-card>

            <!-- Channel Manager Card -->
            <b-card v-if="sectionVisibility.channelManager" class="mb-4">
              <h4 class="card-title border-bottom pb-2 mb-3">
                {{ $t("Channel Manager Settings") }}
              </h4>
              <div class="row">
                <div class="col-md-4 mb-3">
                  <b-form-group :label="$t('Auto Sync Unit')">
                    <toggle-button
                      v-model="formData.auto_sync_unit"
                      :disabled="!isEditing"
                      :sync="true"
                      :labels="{ checked: 'On', unchecked: 'Off' }"
                      :width="100"
                      :height="33"
                      color="#82C7EB"
                    />
                  </b-form-group>
                </div>
                <div class="col-md-4 mb-3">
                  <b-form-group :label="$t('Auto Sync Rate')">
                    <toggle-button
                      v-model="formData.auto_sync_rate"
                      :disabled="!isEditing"
                      :sync="true"
                      :labels="{ checked: 'On', unchecked: 'Off' }"
                      :width="100"
                      :height="33"
                      color="#82C7EB"
                    />
                  </b-form-group>
                </div>
                <div class="col-md-4 mb-3">
                  <b-form-group :label="$t('Auto Sync AVR')">
                    <toggle-button
                      v-model="formData.auto_sync_avr"
                      :disabled="!isEditing"
                      :sync="true"
                      :labels="{ checked: 'On', unchecked: 'Off' }"
                      :width="100"
                      :height="33"
                      color="#82C7EB"
                    />
                  </b-form-group>
                </div>
              </div>
            </b-card>

            <!-- Sales Settings Card -->
            <b-card v-if="sectionVisibility.salesSettings" class="mb-4">
              <h4 class="card-title border-bottom pb-2 mb-3">
                {{ $t("Sales Settings") }}
              </h4>
              <div class="row">
                <div class="col-md-4 mb-3">
                  <b-form-group :label="$t('Tip Rate 1')">
                    <currency-input
                      name="Tip Rate 1"
                      class="info-tip-rate-input"
                      currency-symbol="%"
                      :size="6"
                      :amount.sync="formData.tip_rate_1"
                      :disabled="!isEditing"
                      :field-state="fieldState.tip_rate_1"
                    />
                  </b-form-group>
                </div>
                <div class="col-md-4 mb-3">
                  <b-form-group :label="$t('Tip Rate 2')">
                    <currency-input
                      name="Tip Rate 2"
                      class="info-tip-rate-input"
                      currency-symbol="%"
                      :size="6"
                      :amount.sync="formData.tip_rate_2"
                      :disabled="!isEditing"
                      :field-state="fieldState.tip_rate_2"
                    />
                  </b-form-group>
                </div>
                <div class="col-md-4 mb-3">
                  <b-form-group :label="$t('Tip Rate 3')">
                    <currency-input
                      name="Tip Rate 3"
                      class="info-tip-rate-input"
                      currency-symbol="%"
                      :size="6"
                      :amount.sync="formData.tip_rate_3"
                      :disabled="!isEditing"
                      :field-state="fieldState.tip_rate_3"
                    />
                  </b-form-group>
                </div>
              </div>
            </b-card>
          </b-form>
        </section>

        <input
          style="display: none"
          ref="file-input"
          type="file"
          @change="onFileChanged"
        />

        <image-upload-modal ref="upload-modal" />
      </div>
    </div>
  </SettingsLayout>
</template>

<script>
import API from "@/api";
import apiBase from "@/utils/api-base";
import LocaleSwitcher from "@/components/LocaleSwitcher";
import AddPhotoIcon from "@/assets/icons/md_add_photo.svg";
import axios from "axios";
import SettingsLayout from "@/components/layouts/SettingsLayout.vue";

export default {
  name: "Information",

  components: {
    AddPhotoIcon,
    CurrencyInput: () => import("@/components/widgets/CurrencyInput"),
    GoogleAddressInput: () => import("@/components/GoogleAddressInput"),
    ImageUploadModal: () => import("@/components/modals/ImageUploadModal"),
    InfoRow: () => import("@/components/InfoRow"),
    PhoneInput: () => import("@/components/widgets/PhoneInput"),
    PhotoUploadContainer: () => import("@/components/PhotoUploadContainer"),
    SketchPicker: () => import("vue-color").then((res) => res.Sketch),
    SpinnerLoader: () => import("@/components/widgets/SpinnerLoader"),
    LocaleSwitcher,
    SettingsLayout,
  },

  mounted() {
    this.getCurrentVendor();
  },

  data() {
    return {
      // update here to enable new sections as you roll them out.
      sectionVisibility: {
        payments: false,
        amenities: false,
        socialMedia: false,
        directBooking: false,
        channelManager: false,
        salesSettings: false,
      },
      currency_options: [
        { value: "$", text: "US Dollar USD" },
        { value: "€", text: "Euro EUR" },
      ],
      formData: {
        latitude: 34.134728,
        longitude: -116.3130661,
        direct_booking_enabled: false,
      },
      phone: {
        code: "",
        number: "",
      },
      isUpdatingVendor: false,
      isFetchingVendor: false,
      errored: false,
      isEditing: false,
      hover: {
        photo: false,
      },
      placeholders: {
        propertyName: "Property Name",
        email: "Email Address",
        description: "Property Description",
        facebook: "Facebook URL",
        instagram: "Instagram handle",
        twitter: "Twitter handle",
        snapchat: "Snapchat username",
        pinterest: "Pinterest username",
      },
      amenities: [
        "PET FRIENDLY",
        "AIR CONDITIONING",
        "POOL",
        "WIFI",
        "SPA",
        "FREE TOWELS",
      ],
      selectedFile: null,
      showPicker: false,
      stateChecking: false,
      uploadType: "",
      isStripeConnected: false,
    };
  },

  computed: {
    directBookingUrl() {
      return API.BASE_DOMAIN + "/direct/booking/vendor/" + this.formData.id;
    },
    currentLocale() {
      return this.$route.params.locale;
    },
    vendorCountry: function () {
      let addy = this.getVendor.address || null;
      if (!addy) {
        return this.$t("setup here").toUpperCase();
      }
      let country = addy.substring(addy.lastIndexOf(", ") + 2, addy.length);
      return country;
    },

    fieldState() {
      return {
        tip_rate_1: !this.stateChecking ? null : this.formData.tip_rate_1 > 0,

        tip_rate_2: !this.stateChecking ? null : this.formData.tip_rate_2 > 0,

        tip_rate_3: !this.stateChecking ? null : this.formData.tip_rate_3 > 0,

        min_nights: !this.stateChecking ? null : this.formData.min_nights >= 1,
      };
    },
  },

  methods: {
    onClickFileInput(type) {
      this.uploadType = type;

      let fileInput = this.$refs["file-input"];

      if (fileInput) {
        fileInput.click();
      }
    },

    onFileChanged(event) {
      this.selectedFile = event.target.files[0];

      if (this.uploadType === "photo") {
        this.uploadHotelImage();
      } else {
        this.uploadLogoImage();
      }
    },

    onPhoneUpdate() {
      this.formData.phone_number = JSON.stringify(this.phone); // .code + this.phone.number;
    },

    uploadHotelImage() {
      let formData = new FormData();

      formData.append("image", this.selectedFile, this.selectedFile.name);

      this.showImageUploadModal();

      axios
        .post(`${API.BASE_URL}/vendor/image/${this.formData.id}`, formData, {
          headers: {
            Authorization: `Bearer ${API.TOKEN()}`,
            Accept: "application/json",
            "Content-Type": "multipart/form-data",
          },
        })
        .then((res) => {
          this.formData.image_uri = res.data;
        })
        .catch((err) => {
          this.errorNotification(err.message || "Failed to upload image");
        })
        .finally(() => {
          this.hideImageUploadModal();
        });
    },

    uploadLogoImage() {
      let formData = new FormData();

      formData.append("image", this.selectedFile, this.selectedFile.name);

      this.showImageUploadModal();

      axios
        .post(`${API.BASE_URL}/vendor/logo/${this.formData.id}`, formData, {
          headers: {
            Authorization: `Bearer ${API.TOKEN()}`,
            Accept: "application/json",
            "Content-Type": "multipart/form-data",
          },
        })
        .then((res) => {
          this.formData.logo_uri = res.data;
        })
        .catch((err) => {
          this.errorNotification(err.message || "Failed to upload logo image");
        })
        .finally(() => {
          this.hideImageUploadModal();
        });
    },

    updateAddress(_addr, _lat, _lng) {
      console.log(_addr);
      this.formData.address = _addr;

      if (_lat && _lng) {
        this.formData.latitude = _lat;
        this.formData.longitude = _lng;
      }
    },

    handleSubmit(event) {
      event.preventDefault();
      this.showPicker = false;
      this.stateChecking = true;

      if (!this.isEditing) {
        this.isEditing = true;
        return;
      }

      if (
        this.fieldState.tip_rate_1 === false ||
        this.fieldState.tip_rate_2 === false ||
        this.fieldState.tip_rate_3 === false ||
        this.fieldState.min_nights === false
      ) {
        return;
      }

      this.isUpdatingVendor = true;

      let data = Object.assign({}, this.formData);

      delete data["id"];
      delete data["created_at"];
      delete data["deleted_at"];
      delete data["uses_stripe"];
      delete data["uses_virtual_key"];
      delete data["updated_at"];
      delete data["db"];

      data.payment_enabled = data.payment_enabled ? 1 : 0;
      data.discount_enabled = data.discount_enabled ? 1 : 0;
      data.item_enabled = data.item_enabled ? 1 : 0;
      data.email_confirmation = data.email_confirmation ? 1 : 0;
      data.auto_sync_unit = data.auto_sync_unit ? 1 : 0;
      data.auto_sync_rate = data.auto_sync_rate ? 1 : 0;
      data.auto_sync_avr = data.auto_sync_avr ? 1 : 0;
      data.direct_booking_enabled = data.direct_booking_enabled ? 1 : 0;

      apiBase
        .put(`/vendor/${this.formData.id}`, data)
        .then((response) => {
          this.formData = response.data.original;
          this.formData.payment_enabled = !!this.formData.payment_enabled;
          this.formData.discount_enabled = !!this.formData.discount_enabled;
          this.formData.item_enabled = !!this.formData.item_enabled;
          this.formData.email_confirmation = !!this.formData.email_confirmation;
          this.formData.auto_sync_unit = !!this.formData.auto_sync_unit;
          this.formData.auto_sync_rate = !!this.formData.auto_sync_rate;
          this.formData.auto_sync_avr = !!this.formData.auto_sync_avr;
          this.formData.direct_booking_enabled =
            !!this.formData.direct_booking_enabled;
          this.isEditing = false;
        })
        .catch((error) => {
          this.errored = true;
        })
        .finally(() => {
          this.isUpdatingVendor = false;
        });
    },

    getCurrentVendor() {
      this.isFetchingVendor = true;

      apiBase
        .get(`/vendor/current`)
        .then((res) => {
          this.formData = res.data ? res.data : {};
          this.formData.payment_enabled = !!this.formData.payment_enabled;
          this.formData.discount_enabled = !!this.formData.discount_enabled;
          this.formData.item_enabled = !!this.formData.item_enabled;
          this.formData.email_confirmation = !!this.formData.email_confirmation;
          this.formData.auto_sync_unit = !!this.formData.auto_sync_unit;
          this.formData.auto_sync_rate = !!this.formData.auto_sync_rate;
          this.formData.auto_sync_avr = !!this.formData.auto_sync_avr;
          this.formData.direct_booking_enabled =
            !!this.formData.direct_booking_enabled;
        })
        .catch((err) => {
          this.errored = true;
        })
        .finally(() => {
          this.isFetchingVendor = false;
        });
    },

    toggleColorPicker(e) {
      e.preventDefault();
      this.showPicker = !this.showPicker;
    },

    updateColor(color) {
      this.formData.brand_accent_color = color.hex;
    },

    showImageUploadModal() {
      if (this.$refs["upload-modal"]) {
        this.$refs["upload-modal"].show();
      }
    },

    hideImageUploadModal() {
      if (this.$refs["upload-modal"]) {
        this.$refs["upload-modal"].hide();
      }

      if (this.$refs["file-input"]) {
        this.$refs["file-input"].value = null;
      }
    },
  },

  watch: {
    isEditing() {
      this.stateChecking = false;
    },
  },
};
</script>

<style lang="scss">
@import "@/style/_variables.scss";

.information-view {
  padding-top: 20px;
  padding-bottom: 40px;
  background-color: #f5f7fa;
  width: 100%;
  margin-left: 0;

  .centerscreen {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
  }

  .card {
    background-color: #ffffff;
    border-radius: 8px;
    border: none;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);

    .card-body {
      padding: 1.5rem;
    }
  }

  .card-title {
    font-size: 1.25rem;
    color: #82c7eb;
    font-weight: 600;
  }

  label {
    font-weight: 600;
    margin-bottom: 0.25rem;
    color: #555;
    font-size: 0.9rem;
  }

  p {
    margin-bottom: 0;
    color: #333;
  }

  .hotel-information-image-container {
    height: 150px;
    width: 220px;
    background: #f8f9fa;
    border: 1px dashed #ccc;
    border-radius: 6px;
    overflow: hidden;
    position: relative;

    &:hover {
      background: #e9ecef;

      .upload-btn {
        opacity: 1;
      }
    }

    &.logo-image {
      padding: 1px;

      .image-container {
        font-size: 14px;
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        color: #6c757d;
      }
    }

    .upload-btn {
      position: absolute;
      bottom: 10px;
      right: 10px;
      opacity: 0.8;
      transition: opacity 0.2s ease;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
      z-index: 5;
    }

    // Mobile responsive styles
    @media (max-width: 767.98px) {
      height: 100px;
      width: 150px;
      margin: 0 auto;
    }
  }

  // Make the images column more responsive on mobile
  @media (max-width: 767.98px) {
    .d-flex {
      flex-direction: column;

      .ml-4 {
        margin-left: 0 !important;
        margin-top: 1rem;
        width: 100% !important;

        .mb-3 {
          display: flex;
          flex-direction: column;
          align-items: center;
        }
      }
    }
  }

  .hidden {
    display: none !important;
  }

  .color-picker-wrapper {
    display: flex;
    align-items: center;
    position: relative;
  }

  .current-color {
    width: 40px;
    height: 40px;
    border-radius: 4px;
    border: 1px solid #ccc;
    cursor: pointer;
    display: inline-block;
    transition: opacity 0.2s ease;
  }

  .current-color.disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }

  .color-value {
    margin-left: 10px;
    font-size: 14px;
    color: #555;
  }

  .vc-sketch {
    position: absolute;
    right: 0;
    top: 50px;
    z-index: 99;
  }

  // New styles for the updated sections
  .payment-methods,
  .amenities-list {
    .badge {
      font-size: 0.9rem;
      padding: 0.5rem 1rem;
    }
  }

  .tip-rate,
  .min-nights,
  .deposit-policy,
  .booking-url,
  .toggle-value {
    padding: 0.375rem 0.75rem;
    color: #495057;
    font-size: 0.9rem;
  }

  .input-info-field {
    &.input-error {
      border-color: #dc3545;
    }

    &:disabled {
      background-color: #f8f9fa;
      opacity: 0.8;
      cursor: not-allowed;
    }
  }

  .active-toggle {
    margin-top: 0.25rem;

    &.disabled {
      opacity: 0.7;
      cursor: not-allowed;
    }
  }
}
</style>
